import { SortableContext } from "@dnd-kit/sortable";
import { uniq } from "lodash-es";
import { type Maybe } from "purify-ts";
import { type FC, type MutableRefObject, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { formValueSelector, reduxForm } from "redux-form";
import { ActionButtonIntent, type DialogProps, Tag, TagIntent, TriggerButton, VStack } from "widgets";
import { moveItemsInArray } from "widgets/utils";

import { simpleFieldValidation } from "@/app-utils/form.ts";
import { type ActivityParameter, type NfPlaybookParam, WorkflowParameterTypeEnum } from "@/graphql.ts";
import { WorkflowParameterField } from "@/modules/workflow/components/WorkflowParameterField.tsx";
import { extractValue } from "@/modules/workflow/utils/generateWorkflow.ts";
import type { AllParametersTypes, WorkflowErrorParameter, WorkflowParameter } from "@/modules/workflow/utils/types.ts";
import { type ActivityInstanceExtendedParameterWithActivity, buildFieldProps } from "@/redux/modules/playbook-admin.ts";
import { type IAppState } from "@/redux/reducer.js";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { GREY_COLOR, SPECIAL_COLOR } from "@/settings/colors-settings.ts";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { Dialog } from "@/ui-modules/core/overlays/Dialogs.js";
import { ModalControlledOverlay } from "@/ui-modules/core/overlays/index.js";
import CheckboxField, { CheckboxReduxFormField } from "@/ui-modules/core/redux-form/CheckboxField.tsx";

import { SortableItem, SortableList } from "../../core/forms/Sortable.js";
import style from "../../core/forms/styles/Sortable.module.scss";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";

interface P2ParamsConfiguratorProps {
  lockedVisibilityParams: ActivityInstanceExtendedParameterWithActivity[];
  p2ParamConfigurations: ActivityInstanceExtendedParameterWithActivity[];
  setP2ParamConfigurations: (p2ParamConfigurations: ActivityInstanceExtendedParameterWithActivity[]) => void;
}

export interface NfParamConfiguration extends NfPlaybookParam {
  id?: string; // needed for the sortable context
  defaultWorkflowValue?: string;
}

const P2ParamsConfigurator: FC<P2ParamsConfiguratorProps> = (props) => {
  return (
    <div className="experiment-info-screen">
      <FormInputGroup>
        <FormLabel>
          {`${
            props.p2ParamConfigurations.length ? props.p2ParamConfigurations.length : "No "
          } workflow parameter(s) available`}
        </FormLabel>
        <FormInputWidget>
          <div className={"input-ordering"}>
            <SortableList
              onDragEnd={({ active, over }) => {
                props.setP2ParamConfigurations(
                  moveItemsInArray<ActivityInstanceExtendedParameterWithActivity, any, any>(
                    props.p2ParamConfigurations,
                    active,
                    over,
                  ),
                );
              }}
            >
              <SortableContext items={props.p2ParamConfigurations.map((p2ParamConfig) => p2ParamConfig.id)}>
                {props.p2ParamConfigurations.map((p2ParamConfig, index) => (
                  <Item
                    key={`item-${index}`}
                    index={index}
                    p2ParamConfig={p2ParamConfig}
                    p2ParamConfigurations={props.p2ParamConfigurations}
                    setP2ParamConfigurations={props.setP2ParamConfigurations}
                    lockedVisibilityParams={props.lockedVisibilityParams}
                  />
                ))}
              </SortableContext>
            </SortableList>
          </div>
        </FormInputWidget>
      </FormInputGroup>
    </div>
  );
};

interface ItemProps {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  index: number;
  setP2ParamConfigurations: (confs: ActivityInstanceExtendedParameterWithActivity[]) => void;
  p2ParamConfigurations: ActivityInstanceExtendedParameterWithActivity[];
  lockedVisibilityParams: ActivityInstanceExtendedParameterWithActivity[];
}

const Item: FC<ItemProps> = ({
  p2ParamConfig,
  index,
  setP2ParamConfigurations,
  p2ParamConfigurations,
  lockedVisibilityParams,
}) => {
  const [isConfigurationDialogOpen, setConfigurationDialogOpen] = useState<boolean>(false);

  const onConfigChanged = (p2ParamConfig: ActivityInstanceExtendedParameterWithActivity) => {
    setP2ParamConfigurations(
      p2ParamConfigurations.map((p) =>
        p.activity.identifier === p2ParamConfig.activity.identifier &&
        p.parameterDefinition.id === p2ParamConfig.parameterDefinition.id
          ? p2ParamConfig
          : p,
      ),
    );
  };

  return (
    <>
      <SortableItem index={index} removeDisabled id={p2ParamConfig.id}>
        <P2ParamsConfigItem
          p2ParamConfig={p2ParamConfig}
          onVisibleStateChanged={() => {
            setP2ParamConfigurations(
              p2ParamConfigurations.map((p) =>
                // identify by both param and activity IDs
                p.parameterDefinition.id === p2ParamConfig.parameterDefinition.id &&
                p.activity.identifier === p2ParamConfig.activity.identifier
                  ? { ...p, staged: !p.staged }
                  : p,
              ),
            );
          }}
          lockedVisibility={
            !!lockedVisibilityParams.find(
              (p) =>
                p?.activity.identifier === p2ParamConfig?.activity.identifier &&
                p?.parameterDefinition.id === p2ParamConfig?.parameterDefinition.id,
            ) ||
            // if a mandatory value has no default, we force it to stay staged
            (!p2ParamConfig.parameterDefinition.optional && !extractValue(p2ParamConfig.workflowParameter))
          }
        />
        <>
          <P2ParamsTags p2ParamConfig={p2ParamConfig} />
          <TriggerButton
            label="Configure Parameter"
            tooltip={"Configure how this parameter will be shown as input during the Playbook execution "}
            onPress={() => setConfigurationDialogOpen(true)}
            icon={"edit"}
          />
        </>
      </SortableItem>
      {isConfigurationDialogOpen && (
        <ModalControlledOverlay close={() => setConfigurationDialogOpen(false)} isOpen={true}>
          {({ close }) => (
            <ConfigureP2ParamFormDialog
              p2ParamConfig={p2ParamConfig}
              closeFn={close}
              onConfigChanged={onConfigChanged}
            />
          )}
        </ModalControlledOverlay>
      )}
    </>
  );
};

interface P2ParamsConfigItemProps {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  onVisibleStateChanged?: () => void;
  lockedVisibility: boolean;
}

const P2ParamsConfigItem: FC<P2ParamsConfigItemProps> = ({
  p2ParamConfig,
  onVisibleStateChanged,
  lockedVisibility,
}) => {
  return (
    <>
      <P2ParamsVisibilityToggle
        p2ParamConfig={p2ParamConfig}
        onVisibleStateChanged={onVisibleStateChanged}
        lockedVisibility={lockedVisibility}
      />
      <span className={style.label}>
        {p2ParamConfig.parameterDefinition.label}
        <div className={style.sub_label} style={{ opacity: p2ParamConfig.parameterDefinition.description ? 1 : 0.5 }}>
          {p2ParamConfig.parameterDefinition.description || "No description"}
        </div>
      </span>
      <div style={{ paddingRight: "10px" }}>
        <Tag intentColor={TagIntent.Primary} style={{ borderRadius: "2px" }}>
          {p2ParamConfig.activity.label}
        </Tag>
      </div>
    </>
  );
};

const P2ParamsTags: FC<{ p2ParamConfig: ActivityInstanceExtendedParameterWithActivity }> = ({ p2ParamConfig }) => {
  return (
    <>
      {p2ParamConfig.parameterDefinition.type && (
        <Tag
          tooltip={{ title: p2ParamConfig.optional ? "optional" : "mandatory" }}
          intentColor={p2ParamConfig.optional ? TagIntent.Success : TagIntent.Secondary}
        >
          {getParamTypeLabel(p2ParamConfig.parameterDefinition)}
        </Tag>
      )}
    </>
  );
};

interface P2ParamsVisibilityToggleProps {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  onVisibleStateChanged?: (_) => void;
  lockedVisibility: boolean;
}

const P2ParamsVisibilityToggle: FC<P2ParamsVisibilityToggleProps> = ({
  p2ParamConfig,
  onVisibleStateChanged,
  lockedVisibility,
}) => {
  const hidden = !p2ParamConfig.staged;
  return (
    <TriggerButton
      label={"Toggle visibility"}
      tooltip={
        lockedVisibility
          ? "You cannot change the visibility because this parameter is selected as a mapping target or it is mandatory without a default."
          : "Toggle the visibility of this parameter in the Playbook"
      }
      onPress={onVisibleStateChanged}
      isDisabled={lockedVisibility}
      icon={hidden ? "visibility_off" : "visibility"}
      style={{ color: hidden ? GREY_COLOR.WHITE_2 : SPECIAL_COLOR.ACTIVE }}
    />
  );
};

interface ConfigureP2ParamFormDialogProps extends Omit<DialogProps, "children"> {
  p2ParamConfig: ActivityInstanceExtendedParameterWithActivity;
  onConfigChanged: (p2ParamConfig: ActivityInstanceExtendedParameterWithActivity) => void;
  dialogRef: MutableRefObject<any>; // because redux-form is not working well with the ForwardRef ?
}

enum FieldName {
  label = "label",
  description = "description",
  optional = "optional",
  defaultValue = "defaultValue",
}

interface ConfigureP2ParamFormState {
  [FieldName.label]: string;
  [FieldName.description]: string;
  [FieldName.defaultValue]: WorkflowParameter;
}

const configureP2ParamFormName = "configureP2Param";

const ConfigureP2ParamFormDialog = reduxForm<ConfigureP2ParamFormState, ConfigureP2ParamFormDialogProps>({
  form: configureP2ParamFormName,
})((props) => {
  useEffect(() => {
    // init the local state object whch is the base for the default value setting
    const workflowParameter: WorkflowParameter = props.p2ParamConfig.workflowParameter;

    props.initialize({
      [FieldName.label]: props.p2ParamConfig.parameterDefinition.label,
      [FieldName.description]: props.p2ParamConfig.parameterDefinition.description,
      [FieldName.optional]: props.p2ParamConfig.optional,
      [FieldName.defaultValue]: workflowParameter,
    });
  }, []);

  // the list is overhead, but for compatibility reasons with checkSingleProcessorActivityParameter we stick with a list
  const [error, setError] = useState<WorkflowErrorParameter>();
  const [internalError, setInternalError] = useState<boolean>(false);

  // extract the input type so that we can display the correct input for defining default value
  const state = useSelector<IAppState, IAppState>((state) => state);
  const p2Params = state.playbookAdminState.playbookConfigState.p2Params;

  const valueSelector = formValueSelector(configureP2ParamFormName);
  const workflowParameter: WorkflowParameter = valueSelector(state, FieldName.defaultValue);

  useEffect(() => {
    // validate the current workflowParameter value
    const p2ParamConfig = props.p2ParamConfig;
    if (workflowParameter) {
      const result = workflowParameter.schema?.safeParse(workflowParameter.values.extractNullable());
      let newError = undefined;

      if (result?.success) {
        setError(undefined);
      } else {
        newError = {
          type: "parameter",
          nodeId: p2ParamConfig.activity.identifier,
          parameterId: p2ParamConfig.parameterDefinition.id,
          message: result?.error.issues[0].message,
          code: result?.error.issues[0].params?.code ?? result?.error.issues[0].code,
        } satisfies WorkflowErrorParameter;
      }

      setError(newError);
    }
  }, [workflowParameter]);

  const getSelectedGenomes = useCallback(() => {
    const genomeIds = p2Params
      ?.filter((param) => param.parameterDefinition.type === WorkflowParameterTypeEnum.Genome)
      .map((param) => param.workflowParameter?.values.extractNullable())
      .filter((v): v is number => typeof v === "number");
    return uniq(genomeIds) ?? [];
  }, [p2Params]);

  const setValue = useCallback(
    (values: Maybe<AllParametersTypes>) => {
      props.change(FieldName.defaultValue, { ...workflowParameter, values: values });
    },
    [workflowParameter],
  );

  return (
    <Dialog
      headerProps={{
        title: "Configure Your Playbook Input",
      }}
      closeFn={props.closeFn}
      mainActionButtonProps={{
        label: "Done",
        onPress: props.handleSubmit(handleStoreParamConfig),
        intent: ActionButtonIntent.Primary,
        isDisabled: error || internalError,
      }}
      footerMessage={{ message: props.error }}
    >
      <VStack>
        <InputReduxFormField
          componentClass="input"
          name={FieldName.label}
          label={"Label"}
          component={InputField}
          validate={[simpleFieldValidation]}
        />
        <InputReduxFormField
          componentClass="input"
          name={FieldName.description}
          label={"Description"}
          component={InputField}
        />
        <CheckboxReduxFormField
          name={FieldName.optional}
          label="Optional"
          disabled={!props.p2ParamConfig.parameterDefinition.optional}
          component={CheckboxField}
        />
        {workflowParameter && (
          <WorkflowParameterField
            key={`${props.p2ParamConfig.activity.identifier}-${props.p2ParamConfig.parameterDefinition.id}`}
            parameter={buildFieldProps(props.p2ParamConfig)}
            sampleTypeId={state.playbookAdminState.playbookConfigState.sampleTypeId}
            error={error}
            value={workflowParameter?.values?.extractNullable()}
            setValue={(values) => {
              setValue(values);
            }}
            validateSingleParameter={() => {}} // we do not use this here because it is called to early, before the value is updated in the redux state
            getSelectedGenomesIds={getSelectedGenomes}
            onInternalErrorChange={setInternalError}
            isPlaybookContext={true}
          />
        )}
      </VStack>
    </Dialog>
  );
});

function handleStoreParamConfig(values, _dispatch: ReduxDispatch, props: ConfigureP2ParamFormDialogProps) {
  const workflowParameter = {
    ...props.p2ParamConfig.workflowParameter,
    values: (values[FieldName.defaultValue] as WorkflowParameter).values,
  };
  const realNewValue = extractValue(workflowParameter);

  props.onConfigChanged({
    ...props.p2ParamConfig,
    // if the value was removed and the parameter is mandatory, force the parameter to staged (it must be visible)
    staged: !realNewValue && !props.p2ParamConfig.parameterDefinition.optional ? true : props.p2ParamConfig.staged,
    optional: values[FieldName.optional],
    parameterDefinition: {
      ...props.p2ParamConfig.parameterDefinition,
      label: values[FieldName.label]?.trim(),
      description: values[FieldName.description]?.trim(),
    },
    workflowParameter: workflowParameter,
  });
  props.closeFn();
}

export const getParamTypeLabel = (p2ParamConfig: ActivityParameter) => {
  switch (p2ParamConfig.type) {
    case WorkflowParameterTypeEnum.Text:
      // how to build this correctly in TS? Or rather suppress?
      return p2ParamConfig.stringConstraints?.controlledValues ? "Controlled Text" : "Free Text";
    case WorkflowParameterTypeEnum.Number:
      return p2ParamConfig.numberConstraints?.controlledValues ? "Controlled Number" : "Free Number";
    case WorkflowParameterTypeEnum.Boolean:
      return "Boolean";
    case WorkflowParameterTypeEnum.Date:
      return "Date";
    case WorkflowParameterTypeEnum.Sample:
      return "Sample";
    case WorkflowParameterTypeEnum.GenomicRegion:
      return "Genomic region";
    case WorkflowParameterTypeEnum.File:
      return "File";
      break;
    case WorkflowParameterTypeEnum.Genome:
      return "Genome";
      break;
    default:
      return "Unknown";
  }
};

export default P2ParamsConfigurator;
